import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './CommonStyles.css';
import './EditShippingModal.css';
import apiService from '../../services/api';

const EditShippingModal = ({ sale, onClose, onUpdate, onShowSuccessMessage }) => {
  const [formData, setFormData] = useState({
    shippingDetails: '',
    shippingAddress: '',
    shippingStatus: '',
    deliveredTo: '',
    deliveryPerson: '',
    shippingNote: '',
    documents: []
  });
  const [activities, setActivities] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchUsersList = async () => {
      try {
        const userList = await apiService.getUsers();
        setUsers(userList || []);
      } catch (err) {
        /* console.error('Error fetching users:', err); */
      }
    };
    fetchUsersList();
  }, []);

  useEffect(() => {
    if (!sale) return;

    const populateData = (apiData) => {
      setFormData({
        shippingDetails: apiData.shipping_details || '',
        shippingAddress: apiData.shipping_address || '',
        shippingStatus: apiData.shipping_status || '',
        deliveredTo: apiData.delivered_to || '',
        deliveryPerson: apiData.delivery_person || apiData.delivery_person_id || '',
        shippingNote: apiData.shipping_note || apiData.additional_notes || '',
        documents: []
      });

      if (apiData.shipping_activities) {
        setActivities(apiData.shipping_activities);
      }
    };

    populateData(sale.apiData || sale);

    const fetchFullDetails = async () => {
      try {
        if (sale.id) {
          const res = await apiService.getPurchaseOrder(sale.id);
          if (res && res.data) {
            populateData(res.data);
          }
        }
      } catch (err) {
        /* ignore */
      }
    };
    fetchFullDetails();
  }, [sale]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...files]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const saleId = sale.id;

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('shipping_details', formData.shippingDetails);
      submitData.append('shipping_address', formData.shippingAddress);
      submitData.append('shipping_status', formData.shippingStatus);
      submitData.append('delivered_to', formData.deliveredTo);
      submitData.append('delivery_person', formData.deliveryPerson);
      submitData.append('shipping_note', formData.shippingNote);

      formData.documents.forEach((file, index) => {
        submitData.append(`documents[${index}]`, file);
      });

      // Call API to update shipping (purchase order vs sell)
      if (sale.ref_no || sale.type === 'purchase_order' || sale.type === 'purchase') {
        await apiService.updatePurchaseOrderShipping(saleId, submitData);
      } else {
        await apiService.updateShipping(saleId, submitData);
      }

      // Show success message
      toast.success('Shipping details updated successfully!');
      
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      /* console.error('Error updating shipping:', error); */
      // Show error message
      toast.error('Failed to update shipping details. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!sale) return null;

  const displayRefNo = sale.ref_no || sale.invoiceNo || sale.invoice_no || sale.id || '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container edit-shipping-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Shipping - {displayRefNo}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-content edit-shipping-content">
            <div className="shipping-form-grid">
              {/* Shipping Details */}
              <div className="form-group">
                <label>Shipping Details:*</label>
                <textarea
                  name="shippingDetails"
                  value={formData.shippingDetails}
                  onChange={handleInputChange}
                  placeholder="Shipping Details"
                  rows="2"
                />
              </div>

              {/* Shipping Address */}
              <div className="form-group">
                <label>Shipping Address:</label>
                <textarea
                  name="shippingAddress"
                  value={formData.shippingAddress}
                  onChange={handleInputChange}
                  placeholder="Shipping Address"
                  rows="2"
                />
              </div>

              {/* Shipping Status */}
              <div className="form-group">
                <label>Shipping Status:</label>
                <select
                  name="shippingStatus"
                  value={formData.shippingStatus}
                  onChange={handleInputChange}
                >
                  <option value="">Please Select</option>
                  <option value="ordered">Ordered</option>
                  <option value="packed">Packed</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Delivered To */}
              <div className="form-group">
                <label>Delivered To:</label>
                <input
                  type="text"
                  name="deliveredTo"
                  value={formData.deliveredTo}
                  onChange={handleInputChange}
                  placeholder="Delivered To"
                />
              </div>

              {/* Delivery Person */}
              <div className="form-group">
                <label>Delivery Person:</label>
                <select
                  name="deliveryPerson"
                  value={formData.deliveryPerson}
                  onChange={handleInputChange}
                >
                  <option value="">Please Select</option>
                  {users.map(u => {
                    const fullName = `${u.surname || ''} ${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
                    return (
                      <option key={u.id} value={u.id}>
                        {fullName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Shipping Note */}
              <div className="form-group full-width">
                <label>Shipping note:</label>
                <textarea
                  name="shippingNote"
                  value={formData.shippingNote}
                  onChange={handleInputChange}
                  placeholder="Shipping note"
                  rows="2"
                />
              </div>

              {/* Shipping Documents */}
              <div className="form-group full-width">
                <label>Shipping Documents:</label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="shipping-documents"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="shipping-documents" className="file-upload-label">
                    <div className="upload-placeholder">
                      Drop files here to upload
                    </div>
                  </label>
                  {formData.documents.length > 0 && (
                    <div className="uploaded-files">
                      {formData.documents.map((file, index) => (
                        <div key={index} className="file-item">
                          📄 {file.name}
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="file-help-text">No attachment found</p>
                </div>
              </div>
            </div>

            {/* Activities Section */}
            <div className="activities-section">
              <h3>Activities:</h3>
              <div className="activities-table-wrapper">
                <table className="activities-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Action</th>
                      <th>By</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.length > 0 ? (
                      activities.map((activity, index) => (
                        <tr key={index}>
                          <td>{activity.date}</td>
                          <td>{activity.action}</td>
                          <td>{activity.by}</td>
                          <td>{activity.note}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center' }}>No records found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditShippingModal;
